<template>
    <div ref="scrollContainer" class="library-header-actions">
        <div class="library-header-actions-container">
            <Tooltip
                v-if="userCanPublishDeliverableToAdServer"
                transfer-class-name="library-header__tooltip"
                placement="top"
                :transfer="true"
                max-width="200"
                :disabled="!publishDisabledToolTip"
            >
                <PublishDropdownButton
                    :deliverables="selectedDeliverables"
                    :item-to-change-number="numberOfSelectedDeliverables"
                    :is-disabled="!isPublishButtonEnabled"
                    :show-publish-to-opendc="showPublishToOpendc"
                    :last-job="lastJob"
                    @refetchDeliverables="emitRefetchDeliverables"
                    @publishedStatusHasBeenSet="emitBulkPublishedStatusHasBeenSet"
                    @showPublishModal="emitShowPublishModal"
                />
                <template #content>
                    {{ publishDisabledToolTip }}
                </template>
            </Tooltip>

            <SetDeliverablesVisibleToDropdown
                v-if="userCanChangeDeliverableVisibility"
                :deliverables="selectedDeliverables"
                :item-to-change-number="numberOfSelectedDeliverables"
            />

            <SetDeliverablesStatusDropdown
                v-if="userCanChangeDeliverableQAStatus"
                :deliverables="selectedDeliverables"
                :item-to-change-number="numberOfSelectedDeliverables"
                @statusHasBeenSet="emitBulkQaStatusHasBeenSet"
            />

            <Divider type="vertical" class="divider" />

            <Tooltip
                v-if="preflightPlatform"
                transfer-class-name="library-header__tooltip"
                placement="top"
                :transfer="true"
                max-width="200"
                :disabled="!sendToPreflightButtonTooltip"
            >
                <Button
                    data-testid="library-header__pre-flight-button"
                    icon="md-send"
                    :disabled="!isSendToPreflightButtonEnabled"
                    :loading="isSendingToPreflight"
                    @click="sendToPreFlight"
                >
                    Send to pre-flight
                </Button>
                <template #content>
                    {{ sendToPreflightButtonTooltip }}
                </template>
            </Tooltip>

            <Tooltip
                v-if="userCanShareDeliverable"
                transfer-class-name="library-header__tooltip"
                placement="top"
                :transfer="true"
                max-width="200"
                :disabled="!shareDisabledToolTip"
            >
                <Button
                    :disabled="!isShareButtonEnabled"
                    icon="ios-link"
                    data-testid="library-header__share-button"
                    @click="showSharePreviewModal"
                >
                    Share
                </Button>
                <template #content>
                    {{ shareDisabledToolTip }}
                </template>
            </Tooltip>

            <Button
                :type="bulkApprovaButtonType"
                :disabled="!isReviewButtonEnabled"
                icon="ios-text-outline"
                data-testid="library-header__review-button"
                @click="showBulkApproval"
            >
                Review
            </Button>

            <Button
                v-if="userCanDownloadDeliverable"
                :disabled="!isDownloadButtonEnabled"
                icon="md-download"
                data-testid="library-header__download-button"
                @click="showDownloadModal"
            >
                Download
            </Button>
        </div>
    </div>
</template>

<script lang="ts">
import { type PropType, defineComponent } from "vue";
import { mapStores } from "pinia";

import type { PlatformSettings } from "hoxton-types";
import { PUBLISH_PROVIDERS, type PublishProvidersType } from "hoxton-shared/enums/exports";
import { TemplateType } from "hoxton-shared/enums/masterTemplate";
import { USER_ACTIONS } from "hoxton-shared/enums/user";

import type { ConstructedDeliverable } from "@/types/types";

import PublishDropdownButton from "@/components/Campaign/Publish/PublishDropdownButton.vue";
import SetDeliverablesStatusDropdown from "@/components/Campaign/SetDeliverablesStatusDropdown.vue";
import SetDeliverablesVisibleToDropdown from "@/components/Campaign/SetDeliverablesVisibleToDropdown.vue";

import { publishDeliverables } from "@/services/deliverables/publish";

import { JobsAction, type JobNotification } from "@/store/modules/jobs";
import { NotificationsAction } from "@/store/modules/notifications";
import { useCreativeLibraryFiltersStore } from "@/store/pinia/creativeLibraryFilters";

import { JobStatus } from "@/enums/jobs";
import { NotificationStatus, NotificationTypes } from "@/enums/notifications";
import { useGA4Tracking } from "@/composables/useGA4Tracking";
import GA4 from "@/enums/ga4Events";
import { TemplateTypeLabel } from "@/enums/masterTemplate";
import { userCan } from "@/services/Auth";

export default defineComponent({
    name: "LibraryActionsHeader",
    components: {
        PublishDropdownButton,
        SetDeliverablesStatusDropdown,
        SetDeliverablesVisibleToDropdown
    },

    props: {
        selectedDeliverables: {
            required: true,
            type: Array as PropType<ConstructedDeliverable[]>
        },
        totalItems: {
            required: true,
            type: Number
        },
        lastJob: {
            required: false,
            type: Object as PropType<JobNotification>,
            default: null
        },
        campaignFeed: {
            required: true,
            type: Array as PropType<Record<string, string>[]>
        }
    },

    emits: [
        "bulkPublishedStatusHasBeenSet",
        "bulkQaStatusHasBeenSet",
        "showPublishModal",
        "showSharePreviewModal",
        "showBulkApproval",
        "showDownloadModal",
        "refetchDeliverables"
    ],

    setup() {
        const ga4 = useGA4Tracking();

        return {
            ga4
        };
    },

    data() {
        return {
            isSendingToPreflight: false
        };
    },

    computed: {
        ...mapStores(useCreativeLibraryFiltersStore),

        bulkApprovaButtonType() {
            return userCan(USER_ACTIONS.canChangeDeliverableQAStatus, {
                clientId: this.clientId,
                campaignId: this.campaignId
            })
                ? "default"
                : "primary";
        },

        isLastJobComplete() {
            return this.lastJob?.status === JobStatus.COMPLETE;
        },

        isSendToPreflightButtonEnabled() {
            return (
                this.hasPreflightEnabledAndConfigured &&
                this.hasDeliverablesSelected &&
                !this.isSendingToPreflight &&
                !this.isCampaignLocked &&
                !this.isCampaignIndesign
            );
        },

        isShareButtonEnabled() {
            return this.hasDeliverablesSelected && (this.isCampaignHtml5 || this.isCampaignPsd);
        },

        isPublishButtonEnabled() {
            return this.hasDeliverablesSelected && this.isCampaignHtml5;
        },

        isReviewButtonEnabled() {
            return this.hasDeliverablesSelected;
        },

        isDownloadButtonEnabled() {
            return this.hasDeliverablesSelected;
        },

        showPublishToOpendc() {
            return (
                this.isLastJobComplete &&
                this.validateSelectedDeliverables(this.selectedDeliverables, this.campaignFeed) &&
                !!this.$store.state.campaign.opendcPlatformSettings?.campaignType
            );
        },

        campaignId() {
            return this.$route.params.campaignId as string;
        },

        clientId() {
            return this.$route.params.clientId as string;
        },

        hasDeliverablesSelected() {
            return this.selectedDeliverables.length > 0;
        },

        numberOfSelectedDeliverables() {
            if (this.creativeLibraryFiltersStore.allSelectedMode) {
                return this.totalItems;
            }

            return this.selectedDeliverables.length;
        },

        isCampaignLocked() {
            return this.$store.state.campaign.isLocked;
        },

        hasCreativeXEnabled() {
            return this.$store.state.ui.currentClient?.creativeXPlatformSettings?.enabled ?? false;
        },

        hasCreativeXConfigured() {
            const settings = this.$store.state.campaign.creativeXPlatformSettings;

            return !!(settings?.marketCode && settings?.channel);
        },

        hasSataliaEnabled() {
            return this.$store.state.ui.currentClient?.sataliaPlatformSettings?.enabled ?? false;
        },

        hasSataliaConfigured() {
            return !!this.$store.state.campaign.sataliaPlatformSettings?.assetType;
        },

        isPreflightConfigured() {
            if (this.hasSataliaEnabled) {
                return this.hasSataliaConfigured;
            }

            if (this.hasCreativeXEnabled) {
                return this.hasCreativeXConfigured;
            }

            return false;
        },

        preflightPlatform(): typeof PUBLISH_PROVIDERS.SATALIA | typeof PUBLISH_PROVIDERS.CREATIVEX | null {
            if (this.hasSataliaEnabled) {
                return PUBLISH_PROVIDERS.SATALIA;
            }

            if (this.hasCreativeXEnabled) {
                return PUBLISH_PROVIDERS.CREATIVEX;
            }

            return null;
        },

        preflightName() {
            return this.preflightPlatform === PUBLISH_PROVIDERS.CREATIVEX ? "CreativeX" : "Pre-flight";
        },

        hasPreflightEnabledAndConfigured() {
            return !!this.preflightPlatform && this.isPreflightConfigured;
        },

        sendToPreflightButtonTooltip() {
            if (!this.hasDeliverablesSelected) {
                return "";
            }

            if (this.isCampaignIndesign) {
                return "The preflight feature is not available for InDesign campaigns";
            }

            if (this.isCampaignLocked) {
                return "Campaign is locked";
            }

            if (this.preflightPlatform === PUBLISH_PROVIDERS.SATALIA && !this.hasSataliaConfigured) {
                return "Admin has to configure Pre-flight settings for the campaign.";
            }

            if (this.preflightPlatform === PUBLISH_PROVIDERS.CREATIVEX && !this.hasCreativeXConfigured) {
                return "Admin has to configure CreativeX settings for the campaign.";
            }

            if (this.isSendingToPreflight) {
                return "Sending to pre-flight...";
            }

            return "";
        },

        shareDisabledToolTip() {
            if (!this.hasDeliverablesSelected) {
                return "";
            }

            return `The share feature is not available for ${TemplateTypeLabel[this.$store.state.campaign.campaignType as keyof typeof TemplateTypeLabel]} campaigns`;
        },

        publishDisabledToolTip() {
            if (!this.hasDeliverablesSelected) {
                return "";
            }

            return `The publish feature is not available for ${TemplateTypeLabel[this.$store.state.campaign.campaignType as keyof typeof TemplateTypeLabel]} campaigns`;
        },

        isCampaignHtml5() {
            return this.$store.state.campaign.campaignType === TemplateType.HTML5;
        },

        isCampaignPsd() {
            return this.$store.state.campaign.campaignType === TemplateType.PSD;
        },

        isCampaignIndesign() {
            return this.$store.state.campaign.campaignType === TemplateType.INDESIGN;
        },

        userCanPublishDeliverableToAdServer() {
            return userCan(USER_ACTIONS.canPublishDeliverableToAdServer, {
                clientId: this.clientId,
                campaignId: this.campaignId
            });
        },

        userCanChangeDeliverableVisibility() {
            return userCan(USER_ACTIONS.canChangeDeliverableVisibility, {
                clientId: this.clientId,
                campaignId: this.campaignId
            });
        },

        userCanChangeDeliverableQAStatus() {
            return userCan(USER_ACTIONS.canChangeDeliverableQAStatus, {
                clientId: this.clientId,
                campaignId: this.campaignId
            });
        },

        userCanShareDeliverable() {
            return userCan(USER_ACTIONS.canShareDeliverable, {
                clientId: this.clientId,
                campaignId: this.campaignId
            });
        },

        userCanDownloadDeliverable() {
            return userCan(USER_ACTIONS.canDownloadDeliverable, {
                clientId: this.clientId,
                campaignId: this.campaignId
            });
        }
    },

    mounted() {
        (this.$refs.scrollContainer as HTMLElement).addEventListener("wheel", this.handleWheel, { passive: false });
    },
    beforeUnmount() {
        (this.$refs.scrollContainer as HTMLElement).removeEventListener("wheel", this.handleWheel);
    },

    methods: {
        handleWheel(event: WheelEvent) {
            if (Math.abs(event.deltaY) > Math.abs(event.deltaX)) {
                event.preventDefault();
                (this.$refs.scrollContainer as HTMLElement).scrollLeft += event.deltaY;
            }
        },

        emitRefetchDeliverables() {
            this.$emit("refetchDeliverables");
        },

        emitBulkPublishedStatusHasBeenSet() {
            this.$emit("bulkPublishedStatusHasBeenSet");
        },

        emitBulkQaStatusHasBeenSet() {
            this.$emit("bulkQaStatusHasBeenSet");
        },

        emitShowPublishModal(platformAction: {
            platform: PublishProvidersType;
            action: "publish" | "unpublish" | "rebuild";
        }) {
            this.$emit("showPublishModal", platformAction);
        },

        showBulkApproval() {
            this.$emit("showBulkApproval");
        },

        showSharePreviewModal() {
            this.$emit("showSharePreviewModal");
        },

        async sendToPreFlight() {
            if (this.selectedDeliverables.length === 0) {
                return;
            }

            if (!this.preflightPlatform) {
                return;
            }

            const platformName = this.preflightName;

            this.isSendingToPreflight = true;

            this.ga4.trackFeatureEvent({
                featureEvent: GA4.EVENT_TYPES.FEATURE_ACTIVATE,
                featureCategory: GA4.FEATURE_CATEGORIES.PREFLIGHT,
                featureContext: `Send to ${platformName}`
            });

            try {
                const job = await publishDeliverables({
                    campaignId: this.campaignId,
                    platform: this.preflightPlatform,
                    deliverables: this.selectedDeliverables,
                    libraryFilters: this.creativeLibraryFiltersStore.getComputedLibraryFilters,
                    isRendering: false,
                    rebuild: false
                });

                const notificationId = await this.$store.dispatch(NotificationsAction.Add, {
                    deliverablesCount: this.selectedDeliverables.length,
                    platform: this.preflightPlatform,
                    status: NotificationStatus.InProgress,
                    type: NotificationTypes.Preflight,
                    job
                });

                this.$store.dispatch(JobsAction.SetNotificationIdByActiveJobId, {
                    jobId: job._id,
                    notificationId
                });

                const message =
                    this.preflightPlatform === PUBLISH_PROVIDERS.SATALIA
                        ? `Ads started to be sent to ${platformName}. Please check the notifications for updates.`
                        : `Ads started to be sent to ${platformName} pre-flight. Please check the notifications for updates.`;

                this.$snackbar.success(message);

                this.ga4.trackFeatureEvent({
                    featureEvent: GA4.EVENT_TYPES.FEATURE_COMPLETE,
                    featureCategory: GA4.FEATURE_CATEGORIES.PREFLIGHT,
                    featureContext: `Send to ${platformName}`
                });
            } catch (error) {
                console.error("Error sending ads to pre-flight:", error);
                this.ga4.trackError({
                    errorMessage: (error as Error).message,
                    errorType: GA4.ERROR_TYPES.SYSTEM_ERROR,
                    origin: GA4.ERROR_ORIGINS.BACKEND,
                    featureName: GA4.FEATURE_CATEGORIES.PREFLIGHT
                });
                this.$snackbar.error("Error sending ads to pre-flight");
            }

            this.isSendingToPreflight = false;
        },

        showDownloadModal() {
            this.$emit("showDownloadModal");
        },

        validateSelectedDeliverables(selectedDeliverables: ConstructedDeliverable[], feed: Record<string, string>[]) {
            const feedUniqueIds = new Set(feed.map(f => f.Unique_ID));
            const selectedDeliverablesUniqueIds = new Set(
                selectedDeliverables.map(deliverable => `${deliverable.reportingLabel}_${deliverable.hash}`)
            );

            const selectedDeliverablesFeedUrls = new Set(
                selectedDeliverables.map(
                    deliverable =>
                        (deliverable.publishedStatus as Record<PublishProvidersType, PlatformSettings | null>)?.[
                            PUBLISH_PROVIDERS.OPENDC
                        ]?.xmlFeedUrl
                )
            );

            // The case when user changes the editables in Editor and the rendering is not reflecting the latest changes
            if (selectedDeliverablesFeedUrls.has(null) || selectedDeliverablesFeedUrls.has(undefined)) {
                return false;
            }

            // Check if every item in selectedDeliverablesUniqueIds is in feedUniqueIds
            for (const id of selectedDeliverablesUniqueIds) {
                if (!feedUniqueIds.has(id)) {
                    return false;
                }
            }

            return true;
        }
    }
});
</script>

<style lang="scss" scoped>
@use "@style/variables" as *;

.library-header-actions {
    background-color: $white;
    padding: 10px 20px;
    border-bottom: 1px solid $grey3;
    border-left: 1px solid $grey3;
    overflow-y: auto;

    .library-header-actions-container {
        margin-left: auto;
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: 5px;
        width: max-content;

        .divider {
            height: 30px;
        }
    }
}
</style>
<style lang="scss">
.library-header__tooltip {
    .ivu-tooltip-inner-with-width {
        word-break: break-word;
        text-align: left;
    }
}
</style>
